import { ReactNode, useCallback } from 'react';

import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';

import { commonTranslationKeys } from '@dreryk/react-common';
import { ModalCancelButton, ModalCloseButton, ModalWidth, ShowModalEventInterceptor } from '@dreryk/react-components';
import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';

import { translationKeys } from '../../translations';

import { PatientExternalIdentifierTypeStillValidAgeMonit } from './components/PatientExternalIdentifierTypeStillValidAgeMonit';
import { PatientExternalIdentifierTypeStillValidForm } from './components/PatientExternalIdentifierTypeStillValidForm';
import { PatientExternalIdentifierTypeStillValidConfirmButton } from './components/PatientExternalIdentifierTypeStillValidConfirmButton';
import { StyledPatientExternalIdentifierTypeStillValidEventInterceptorContent } from './PatientExternalIdentifierTypeStillValidEventInterceptor.styled';

type PatientExternalIdentifierTypeStillValidEventInterceptorProps = {
   readonly children: ReactNode;
   readonly patientId: number;
};

export const PatientExternalIdentifierTypeStillValidEventInterceptor = (
   props: PatientExternalIdentifierTypeStillValidEventInterceptorProps
): JSX.Element => {
   const shouldShowModal = useCallback(async () => {
      const patient = await performCqrsQueryGet<PatientSimple>(
         () => {
            const query = new PatientSimpleQuery();
            query.id = Slot.of(props.patientId);
            return query;
         },
         {
            cacheTime: 'never',
         }
      );

      if (!patient.externalIdentifierTypeStillValid) {
         return {
            patientExternalIdentifier: patient.externalIdentifier,
            patientId: patient.id,
         };
      }

      return false;
   }, [props.patientId]);

   return (
      <ShowModalEventInterceptor
         testId={'patientExternalIdentifierTypeStillValid'}
         shouldShowModal={shouldShowModal}
         title={translationKeys.patientIdentifierExpired}
         modalWidth={ModalWidth.WIDE}
         content={({ resolve, reject, result: { patientExternalIdentifier, patientId } }) => {
            return (
               <StyledPatientExternalIdentifierTypeStillValidEventInterceptorContent>
                  <PatientExternalIdentifierTypeStillValidAgeMonit
                     patientExternalIdentifier={patientExternalIdentifier}
                  />
                  <PatientExternalIdentifierTypeStillValidForm patientExternalIdentifier={patientExternalIdentifier}>
                     {({ handleSubmit, formState }) => (
                        <>
                           <PatientExternalIdentifierTypeStillValidConfirmButton
                              patientId={patientId}
                              handleSubmit={handleSubmit}
                              onSuccess={resolve}
                           />
                           <ModalCancelButton
                              label={commonTranslationKeys.cancel}
                              onClick={reject}
                              shouldConfirmExit={formState.isDirty}
                           />
                           <ModalCloseButton onClick={reject} shouldConfirmExit={formState.isDirty} />
                        </>
                     )}
                  </PatientExternalIdentifierTypeStillValidForm>
               </StyledPatientExternalIdentifierTypeStillValidEventInterceptorContent>
            );
         }}
         footerItemsHidden
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
